import React, { useState, useEffect } from 'react';
import { veoCreditAdminService, VeoCreditUserData, VeoSystemOverview } from '../../services/veoCreditAdminService';
import VeoCreditUserDetailsModal from './VeoCreditUserDetailsModal';
import VeoCreditAdjustmentModal from './VeoCreditAdjustmentModal';
import styles from './FuturisticAdmin.module.css';

const VeoCreditAdminManager: React.FC = () => {
  const [users, setUsers] = useState<VeoCreditUserData[]>([]);
  const [systemOverview, setSystemOverview] = useState<VeoSystemOverview | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState<VeoCreditUserData | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Test connection first
      const testResult = await veoCreditAdminService.testConnection();
      if (!testResult.success) {
        setError(`Connection test failed: ${testResult.message}`);
        return;
      }
      
      // Load system overview first (this might return empty data)
      const overviewResponse = await veoCreditAdminService.getVeoSystemOverview();
      setSystemOverview(overviewResponse);
      
      // Load users (might be empty if no one has VEO credits yet)
      const usersResponse = await veoCreditAdminService.searchUsersWithVeoCredits('', 50);
      setUsers(usersResponse.users);
    } catch (err: any) {
      console.error('Failed to load VEO credit data:', err);
      // More user-friendly error message
      if (err.message.includes('500')) {
        setError('Server error loading VEO credits. This might be because no users have purchased VEO credits yet, or there\'s a database connection issue.');
      } else if (err.message.includes('404')) {
        setError('VEO Credits admin endpoints not found. Please check if the backend VEO credit module is properly installed.');
      } else if (err.message.includes('401') || err.message.includes('403')) {
        setError('Access denied. Please check your admin permissions.');
      } else {
        setError('Failed to load VEO credit data. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    try {
      setSearchLoading(true);
      setSearchQuery(query);
      
      const response = await veoCreditAdminService.searchUsersWithVeoCredits(query, 50);
      setUsers(response.users);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUserClick = (user: VeoCreditUserData) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleAdjustCredits = (user: VeoCreditUserData) => {
    setSelectedUser(user);
    setShowAdjustmentModal(true);
  };

  const handleAdjustmentSuccess = () => {
    // Refresh data after successful adjustment
    loadInitialData();
    setShowAdjustmentModal(false);
  };

  const formatUtilization = (user: VeoCreditUserData): { rate: number; color: string } => {
    const rate = veoCreditAdminService.calculateUtilizationRate(user.totalPurchased, user.credits);
    
    let color = '#10b981'; // Green - low usage
    if (rate > 70) color = '#f59e0b'; // Orange - high usage
    if (rate > 90) color = '#ef4444'; // Red - very high usage
    
    return { rate, color };
  };

  if (loading) {
    return (
      <div style={{ color: 'white', padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎬</div>
        <h3>Loading VEO Credit System...</h3>
        <div className={styles.loadingSpinner} style={{ margin: '2rem auto' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: 'white', padding: '2rem' }}>
        <div className={styles.card} style={{ 
          backgroundColor: 'rgba(239, 68, 68, 0.1)', 
          border: '1px solid rgba(239, 68, 68, 0.3)' 
        }}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
            <h3 style={{ color: '#ef4444', margin: '0 0 1rem 0' }}>Error Loading VEO Credits</h3>
            <p style={{ color: '#e2e8f0', margin: '0 0 1.5rem 0' }}>{error}</p>
            <button 
              onClick={loadInitialData}
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ color: 'white', padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 className={styles.testTitle}>🎬 VEO Credits Administration</h1>
        <p style={{ color: '#06b6d4', fontSize: '1.1rem', marginBottom: '1.5rem' }}>
          Manage VEO video generation credits for all users. Assign credits to existing accounts or adjust balances.
        </p>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ 
            backgroundColor: 'rgba(16, 185, 129, 0.1)', 
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '8px',
            padding: '0.5rem 1rem',
            fontSize: '0.9rem'
          }}>
            <span style={{ color: '#10b981' }}>✅ VEO System Active</span>
          </div>
          
          <button 
            onClick={loadInitialData}
            style={{
              backgroundColor: 'rgba(6, 182, 212, 0.2)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              color: '#06b6d4',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 Refresh Data
          </button>
        </div>
      </div>

      {/* System Overview Cards */}
      {systemOverview && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1.5rem', 
          marginBottom: '2rem' 
        }}>
          <div className={styles.card}>
            <h3 style={{ color: '#06b6d4', fontSize: '1rem', marginBottom: '0.5rem' }}>Users with VEO</h3>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
              {systemOverview.overview.totalUsers}
            </p>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.25rem 0 0 0' }}>
              Have purchased credits
            </p>
          </div>
          
          {systemOverview.overview.totalUsersInSystem && (
            <div className={styles.card}>
              <h3 style={{ color: '#f59e0b', fontSize: '1rem', marginBottom: '0.5rem' }}>Users without VEO</h3>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
                {systemOverview.overview.usersWithoutVeoCredits || 0}
              </p>
              <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.25rem 0 0 0' }}>
                Can be assigned credits
              </p>
            </div>
          )}
          
          <div className={styles.card}>
            <h3 style={{ color: '#8b5cf6', fontSize: '1rem', marginBottom: '0.5rem' }}>Credits in System</h3>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
              {veoCreditAdminService.formatCredits(systemOverview.overview.totalCreditsInSystem)}
            </p>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.25rem 0 0 0' }}>
              Available now
            </p>
          </div>
          
          <div className={styles.card}>
            <h3 style={{ color: '#10b981', fontSize: '1rem', marginBottom: '0.5rem' }}>Total Revenue</h3>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
              {veoCreditAdminService.formatCurrency(systemOverview.overview.totalRevenue)}
            </p>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.25rem 0 0 0' }}>
              VEO credit sales
            </p>
          </div>
          
          <div className={styles.card}>
            <h3 style={{ color: '#ef4444', fontSize: '1rem', marginBottom: '0.5rem' }}>Credits Consumed</h3>
            <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
              {veoCreditAdminService.formatCredits(systemOverview.overview.totalCreditsConsumed)}
            </p>
            <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0.25rem 0 0 0' }}>
              Videos generated
            </p>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className={styles.card} style={{ marginBottom: '2rem' }}>
        <div style={{ padding: '1.5rem' }}>
          <h3 style={{ color: 'white', fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
            Search All Users
          </h3>
          <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '1rem' }}>
            Find any user to view or assign VEO credits. Users without VEO credits will show 0 balance.
          </p>
          
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by email address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: 'rgba(15, 23, 42, 0.5)',
                  border: '1px solid rgba(51, 65, 85, 0.5)',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '0.9rem'
                }}
              />
              {searchLoading && (
                <div style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#06b6d4'
                }}>
                  🔄
                </div>
              )}
            </div>
            
            <button
              onClick={() => handleSearch(searchQuery)}
              disabled={searchLoading}
              style={{
                backgroundColor: 'rgba(6, 182, 212, 0.2)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                color: '#06b6d4',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: searchLoading ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                opacity: searchLoading ? 0.5 : 1
              }}
            >
              🔍 Search
            </button>
            
            <button
              onClick={() => handleSearch('')}
              style={{
                backgroundColor: 'rgba(100, 116, 139, 0.2)',
                border: '1px solid rgba(100, 116, 139, 0.3)',
                color: '#64748b',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className={styles.card} style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid rgba(51, 65, 85, 0.5)' }}>
          <h3 style={{ color: 'white', fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
            All Users ({users.length})
          </h3>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
            Click on a user to view details • Use adjust button to assign or modify VEO credits
          </p>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
              <tr>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>User</th>
                <th style={{ textAlign: 'left', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Plan</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Credits</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Purchased</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Consumed</th>
                <th style={{ textAlign: 'right', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Total Spent</th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Utilization</th>
                <th style={{ textAlign: 'center', padding: '1rem', color: '#94a3b8', fontWeight: '500' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? users.map((user, index) => {
                const utilization = formatUtilization(user);
                
                return (
                  <tr 
                    key={user.userId} 
                    style={{ 
                      borderTop: index > 0 ? '1px solid rgba(51, 65, 85, 0.5)' : 'none',
                      transition: 'background-color 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(51, 65, 85, 0.3)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={() => handleUserClick(user)}
                  >
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div>
                          <div style={{ color: 'white', fontWeight: '500' }}>{user.email}</div>
                          <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{user.role}</div>
                        </div>
                        {!user.hasVeoCredits && (
                          <div style={{
                            fontSize: '0.75rem',
                            color: '#f59e0b',
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            borderRadius: '4px',
                            padding: '0.125rem 0.25rem'
                          }}>
                            No VEO
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        color: '#06b6d4', 
                        fontSize: '0.875rem',
                        backgroundColor: 'rgba(6, 182, 212, 0.1)',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        border: '1px solid rgba(6, 182, 212, 0.3)'
                      }}>
                        {user.plan.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <span style={{ 
                        color: user.credits > 0 ? '#10b981' : '#ef4444', 
                        fontWeight: '600', 
                        fontSize: '1.1rem' 
                      }}>
                        {veoCreditAdminService.formatCredits(user.credits)}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#e2e8f0' }}>
                      {veoCreditAdminService.formatCredits(user.totalPurchased)}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#e2e8f0' }}>
                      {veoCreditAdminService.formatCredits(user.totalConsumed)}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#10b981', fontWeight: '500' }}>
                      {veoCreditAdminService.formatCurrency(user.totalSpent)}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        backgroundColor: `${utilization.color}20`,
                        border: `1px solid ${utilization.color}40`,
                        borderRadius: '12px',
                        padding: '0.25rem 0.75rem',
                        fontSize: '0.8rem',
                        color: utilization.color,
                        fontWeight: '500'
                      }}>
                        {utilization.rate}%
                      </div>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAdjustCredits(user);
                        }}
                        style={{
                          backgroundColor: user.hasVeoCredits 
                            ? 'rgba(139, 92, 246, 0.2)' 
                            : 'rgba(16, 185, 129, 0.2)',
                          border: user.hasVeoCredits 
                            ? '1px solid rgba(139, 92, 246, 0.3)' 
                            : '1px solid rgba(16, 185, 129, 0.3)',
                          color: user.hasVeoCredits ? '#8b5cf6' : '#10b981',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: '500'
                        }}
                      >
                        {user.hasVeoCredits ? '⚙️ Adjust' : '🎁 Assign'}
                      </button>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={8} style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: 'white' }}>No Users Found</div>
                    <div style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
                      {searchQuery ? 'Try a different search term or check spelling' : 'No users in the system yet'}
                    </div>
                    {!searchQuery && (
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#64748b',
                        backgroundColor: 'rgba(6, 182, 212, 0.1)',
                        border: '1px solid rgba(6, 182, 212, 0.3)',
                        borderRadius: '6px',
                        padding: '0.75rem',
                        display: 'inline-block',
                        maxWidth: '400px'
                      }}>
                        💡 This interface shows all users in the system. You can assign VEO credits to any user, even if they haven't purchased VEO credits before.
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showUserDetails && selectedUser && (
        <VeoCreditUserDetailsModal
          isOpen={showUserDetails}
          onClose={() => setShowUserDetails(false)}
          userId={selectedUser.userId}
          userEmail={selectedUser.email}
        />
      )}

      {showAdjustmentModal && selectedUser && (
        <VeoCreditAdjustmentModal
          isOpen={showAdjustmentModal}
          onClose={() => setShowAdjustmentModal(false)}
          user={selectedUser}
          onSuccess={handleAdjustmentSuccess}
        />
      )}
    </div>
  );
};

export default VeoCreditAdminManager;
